import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePlaidLink } from "react-plaid-link";
import { toast } from "sonner";
import { Loader2, AlertCircle, Building2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { API_URL } from "@/utils/api";

export default function PlaidOAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receivedRedirectUri, setReceivedRedirectUri] = useState(null);

  // Get the full URL including the oauth_state_id parameter
  const fullRedirectUri = window.location.href;

  useEffect(() => {
    const resumeOAuthFlow = async () => {
      // Check if we have oauth_state_id in URL params (indicates OAuth redirect from bank)
      const oauthStateId = searchParams.get("oauth_state_id");
      
      if (!oauthStateId) {
        setError("Missing OAuth state. Please try connecting your bank again.");
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("auth_token");
        
        // Get a new link token for resuming the OAuth flow
        const response = await fetch(`${API_URL}/api/plaid/link-token`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({})
        });

        if (response.ok) {
          const data = await response.json();
          setLinkToken(data.link_token);
          setReceivedRedirectUri(fullRedirectUri);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || "Failed to resume bank connection");
        }
      } catch (err) {
        console.error("OAuth resume error:", err);
        setError("Failed to resume bank connection. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    resumeOAuthFlow();
  }, [searchParams, fullRedirectUri]);

  const onPlaidSuccess = useCallback(async (public_token, metadata) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/exchange-token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          public_token,
          institution_id: metadata.institution?.institution_id,
          institution_name: metadata.institution?.name
        })
      });

      if (response.ok) {
        toast.success(`${metadata.institution?.name || "Bank"} connected successfully!`);
        navigate("/dashboard/settings/bank", { replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to connect bank");
        setError(error.detail || "Failed to complete bank connection");
        setLoading(false);
      }
    } catch (err) {
      console.error("Error exchanging token:", err);
      toast.error("Failed to complete bank connection");
      setError("Failed to complete bank connection. Please try again.");
      setLoading(false);
    }
  }, [navigate]);

  const onPlaidExit = useCallback((err) => {
    if (err) {
      console.error("Plaid Link error:", err);
      setError("Bank connection was cancelled or failed.");
    } else {
      setError("Bank connection was cancelled.");
    }
    setLoading(false);
  }, []);

  // Configure Plaid Link with OAuth resume parameters
  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
    receivedRedirectUri: receivedRedirectUri // This tells Plaid we're resuming OAuth
  };

  const { open, ready, error: linkError } = usePlaidLink(config);

  // Auto-open Plaid Link when ready with OAuth resume
  useEffect(() => {
    if (linkToken && ready && receivedRedirectUri) {
      open();
    }
  }, [linkToken, ready, receivedRedirectUri, open]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-2xl border-2 border-[#BFFF00]/30 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-[#121212]" />
            </div>
            <Loader2 className="w-8 h-8 animate-spin text-[#121212] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#121212] mb-2">Connecting Your Bank</h2>
            <p className="text-[#121212]/60">Completing secure bank verification...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-2xl border-2 border-red-200 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#121212] mb-2">Connection Failed</h2>
            <p className="text-[#121212]/60 mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => navigate("/dashboard/settings/bank")}
                className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
              >
                Back to Bank Connections
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while Plaid Link opens
  return (
    <div className="min-h-screen bg-[#F7FFE5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-2xl border-2 border-[#BFFF00]/30 shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-8 h-8 text-[#121212]" />
          </div>
          <Loader2 className="w-8 h-8 animate-spin text-[#121212] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#121212] mb-2">Opening Bank Connection</h2>
          <p className="text-[#121212]/60">Please wait while we open the secure bank connection...</p>
        </CardContent>
      </Card>
    </div>
  );
}
